<template>
  <div class="seg" :class="['seg--' + size]" role="tablist">
    <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        role="tab"
        class="seg__btn"
        :class="{ 'seg__btn--active': tab.value === value, 'seg__btn--disabled': tab.disabled }"
        :disabled="tab.disabled"
        :title="tab.disabled ? (tab.disabledHint || '') : ''"
        :aria-selected="tab.value === value ? 'true' : 'false'"
        @click="!tab.disabled && $emit('input', tab.value)"
    >
      <md-icon v-if="tab.icon" class="seg__icon">{{ tab.icon }}</md-icon>
      <span class="seg__label">{{ tab.label }}</span>
    </button>
  </div>
</template>

<script>
/**
 * A simple segmented-control / pill tab switcher. Stateless (v-model driven),
 * so it can be reused for page-level feature switching, editor sections, and
 * compact language pills. The active segment fills with the CRM theme color.
 *
 * Props:
 *  - tabs:  [{ value, label, icon?, disabled?, disabledHint? }]
 *  - value: currently selected value (v-model)
 *  - size:  'md' (default) | 'sm' (compact, e.g. language pills)
 *
 * A tab with `disabled: true` renders greyed-out and is not selectable.
 */
export default {
  name: 'SegmentedTabs',
  props: {
    tabs: { type: Array, required: true },
    value: { type: [String, Number], default: null },
    size: { type: String, default: 'md' }
  }
};
</script>

<style scoped>
.seg {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef0f3;
  border-radius: 999px;
  padding: 4px;
}

.seg__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: none;
  background: transparent;
  color: #5b626b;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 1.1rem;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background .18s ease, color .18s ease, box-shadow .18s ease;
}
.seg__btn:hover { color: #2c2c2c; }
.seg__btn--active {
  background: #343841;
  color: #fff;
  box-shadow: 0 2px 8px rgba(52, 56, 65, 0.25);
}
.seg__btn--active:hover { color: #fff; }

.seg__btn--disabled,
.seg__btn--disabled:hover {
  color: #b6bbc2;
  cursor: not-allowed;
  opacity: 0.7;
}

.seg__icon { font-size: 18px !important; color: inherit !important; }

/* Compact variant for language pills */
.seg--sm { padding: 3px; gap: 3px; }
.seg--sm .seg__btn {
  padding: 0.3rem 0.7rem;
  font-size: 0.75rem;
  letter-spacing: 0.03em;
}
.seg--sm .seg__icon { font-size: 15px !important; }
</style>
